import {
  inject,
  onBeforeUnmount,
  onMounted,
  provide,
  ref,
  type ComponentPublicInstance,
  type InjectionKey,
  type Ref
} from 'vue'

export interface LandingScrollState {
  /** El navbar pasa a estado sólido cuando la página supera los 80px. */
  scrolled: Ref<boolean>
  /** Menú móvil desplegado. */
  mobileOpen: Ref<boolean>
  /** Registra un nodo para la animación de entrada por scroll. */
  registerReveal: (el: Element | ComponentPublicInstance | null) => void
  toggleMobile: () => void
  closeMobile: () => void
  smoothScrollTo: (hash: string) => void
}

export const LandingScrollKey: InjectionKey<LandingScrollState> = Symbol('landing-scroll')

/**
 * Estado y comportamiento de scroll del sitio público.
 *
 * Se inicializa una sola vez en la vista raíz y se comparte con los
 * componentes hijos mediante provide/inject, de modo que el navbar, el hero
 * y los enlaces internos coordinan el mismo estado.
 *
 * `scrolled` y `mobileOpen` son `ref` (no propiedades de un `reactive`):
 * un objeto `reactive` pierde la reactividad al destructurarlo, y el navbar
 * debe reflejar el scroll en tiempo real.
 */
export function provideLandingScroll(): LandingScrollState {
  // Array plano (no reactivo): un Elemento envuelto en un Proxy no es
  // aceptable por IntersectionObserver.observe().
  const revealTargets: Element[] = []

  // Elementos ya revelados y vigiladores de su atributo `class`.
  const revealedEls = new Set<Element>()
  const classWatchers = new Map<Element, MutationObserver>()

  const scrolled = ref(false)
  const mobileOpen = ref(false)

  /**
   * Marca un nodo como visible.
   *
   * Al añadir la clase `visible` de forma imperativa, Vue puede borrarla más
   * adelante: cuando el binding `:class` del elemento cambia (por ejemplo al
   * filtrar los tabs de simuladores o abrir un ítem del FAQ), Vue reescribe
   * `className` completo y la clase añadida a mano desaparece, dejando el
   * bloque invisible para siempre. El MutationObserver vigila el atributo y
   * la restaura de inmediato.
   */
  function markRevealed(el: Element) {
    el.classList.add('visible')
    revealedEls.add(el)
    if (classWatchers.has(el)) return
    const watcher = new MutationObserver(() => {
      if (!el.classList.contains('visible')) el.classList.add('visible')
    })
    watcher.observe(el, { attributes: true, attributeFilter: ['class'] })
    classWatchers.set(el, watcher)
  }

  function registerReveal(el: Element | ComponentPublicInstance | null) {
    // La ref puede resolver a un componente; solo nos interesan los nodos.
    if (!(el instanceof Element) || revealTargets.includes(el)) return
    revealTargets.push(el)
    // Si el nodo aparece después de creado el observer (p. ej. un tab que
    // cambia de contenido), hay que observarlo en el momento.
    observer?.observe(el)
  }

  function toggleMobile() {
    mobileOpen.value = !mobileOpen.value
  }

  function closeMobile() {
    mobileOpen.value = false
  }

  function smoothScrollTo(hash: string) {
    const target = document.querySelector(hash)
    if (!target) return
    const top = target.getBoundingClientRect().top + window.scrollY - 80
    window.scrollTo({ top, behavior: 'smooth' })
    mobileOpen.value = false
  }

  const state: LandingScrollState = {
    scrolled,
    mobileOpen,
    registerReveal,
    toggleMobile,
    closeMobile,
    smoothScrollTo
  }

  let observer: IntersectionObserver | null = null

  function onScroll() {
    scrolled.value = window.scrollY > 80
  }

  onMounted(() => {
    window.addEventListener('scroll', onScroll, { passive: true })
    onScroll()

    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              markRevealed(entry.target)
              observer?.unobserve(entry.target)
            }
          })
        },
        /*
         * `threshold: 0` es deliberado: con un umbral > 0 los elementos
         * `.reveal` más altos que la ventana nunca alcanzarían el porcentaje
         * exigido y quedarían invisibles para siempre. Con 0 basta con que
         * entren un solo píxel en la raíz de observación.
         */
        { threshold: 0, rootMargin: '0px 0px -60px 0px' }
      )
      revealTargets.forEach((el) => observer?.observe(el))
    } else {
      revealTargets.forEach((el) => markRevealed(el))
    }
  })

  onBeforeUnmount(() => {
    window.removeEventListener('scroll', onScroll)
    observer?.disconnect()
    observer = null
    classWatchers.forEach((watcher) => watcher.disconnect())
    classWatchers.clear()
    revealedEls.clear()
  })

  provide(LandingScrollKey, state)
  return state
}

/** Acceso al estado compartido. Debe usarse dentro de un hijo de la vista raíz. */
export function useLandingScroll(): LandingScrollState {
  const state = inject(LandingScrollKey)
  if (!state) {
    throw new Error('useLandingScroll() debe usarse dentro de provideLandingScroll()')
  }
  return state
}
